import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function NernstEquationCalculator() {
  const [standardPotential, setStandardPotential] = useState("");
  const [temperature, setTemperature] = useState("298.15");
  const [electrons, setElectrons] = useState("");
  const [reactionQuotient, setReactionQuotient] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const e0 = parseFloat(standardPotential);
    const t = parseFloat(temperature);
    const n = parseFloat(electrons);
    const q = parseFloat(reactionQuotient);

    if (isNaN(e0) || isNaN(t) || isNaN(n) || isNaN(q) || n <= 0 || t <= 0 || q <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const R = 8.314; // J/(mol·K)
    const F = 96485; // C/mol
    
    // E = E° - (RT/nF)ln(Q)
    const e = e0 - ((R * t) / (n * F)) * Math.log(q);
    
    setResult(e);

    toast({
      title: "Calculation Complete",
      description: `E = ${e.toFixed(4)} V`,
    });
  };

  const reset = () => {
    setStandardPotential("");
    setTemperature("298.15");
    setElectrons("");
    setReactionQuotient("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Nernst Equation Calculator" 
      calculatorPath="/calculator/nernst-equation"
      category="chemistry"
      subcategory="Electrochemistry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Nernst Equation Calculator</CardTitle>
            <CardDescription>
              Calculate cell potential under non-standard conditions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="e0">Standard Potential E° (V)</Label>
                <Input
                  id="e0"
                  type="number"
                  step="any"
                  value={standardPotential}
                  onChange={(e) => setStandardPotential(e.target.value)}
                  placeholder="Enter E°"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature (K)</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="any"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="298.15 K (25°C)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="electrons">Number of Electrons (n)</Label>
                <Input
                  id="electrons"
                  type="number"
                  step="1"
                  value={electrons}
                  onChange={(e) => setElectrons(e.target.value)}
                  placeholder="Electrons transferred"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="q">Reaction Quotient (Q)</Label>
                <Input
                  id="q"
                  type="number"
                  step="any"
                  value={reactionQuotient}
                  onChange={(e) => setReactionQuotient(e.target.value)}
                  placeholder="Q = [products]/[reactants]"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Cell Potential (E)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(4)} V</p>
                    <CopyResultButton value={`${result.toFixed(4)} V`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Nernst Equation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono text-sm">E = E° - (RT/nF)ln(Q)</p>
                <p className="text-sm text-muted-foreground mt-2">
                  R = 8.314 J/(mol·K), F = 96485 C/mol
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
